import {
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    List,
    ListItem,
    ListItemText,
    Paper,
    TextField,
    Typography,
} from '@mui/material';

import { Close } from '@mui/icons-material';
import { create } from 'zustand';
import { t } from 'i18next';
import { useState } from 'react';

type HelpDialogStore = {
    open: boolean;
    availableInvoicings: Array<{ entityId: string; entityNumber: string; integrationIdAndType: string }>;
    selectedInvoicingIds: string[];
    onSubmit: (userQuery: string, selectedInvoicings: Array<{ entityNumber: string; workflowId: string }>) => void;
    close: () => void;
    toggleInvoicingSelection: (invoicingId: string) => void;
};

const useHelpDialogStore = create<HelpDialogStore>((set) => ({
    open: false,
    availableInvoicings: [],
    selectedInvoicingIds: [],
    onSubmit: () => {},
    close: () => set({ open: false }),
    toggleInvoicingSelection: (invoicingId: string) =>
        set((state) => {
            const isSelected = state.selectedInvoicingIds.includes(invoicingId);
            return {
                selectedInvoicingIds: isSelected
                    ? state.selectedInvoicingIds.filter((id) => id !== invoicingId)
                    : [...state.selectedInvoicingIds, invoicingId],
            };
        }),
}));

export const openHelpDialog = (
    initialSelectedIds: string[],
    availableInvoicings: Array<{ entityId: string; entityNumber: string; integrationIdAndType: string }>,
    onSubmit: (userQuery: string, selectedInvoicings: Array<{ entityNumber: string; workflowId: string }>) => void,
) => {
    useHelpDialogStore.setState({
        open: true,
        availableInvoicings,
        selectedInvoicingIds: initialSelectedIds,
        onSubmit,
    });
};

export const HelpDialog = () => {
    const { open, availableInvoicings, selectedInvoicingIds, onSubmit, close, toggleInvoicingSelection } =
        useHelpDialogStore();

    const [userQuery, setUserQuery] = useState('');
    const [searchFilter, setSearchFilter] = useState('');

    const handleSubmit = () => {
        // Find selected invoicings and format them for submission
        const selectedInvoicings = availableInvoicings
            .filter((inv) => selectedInvoicingIds.includes(inv.entityId))
            .map((inv) => ({
                entityNumber: inv.entityNumber,
                workflowId: `${inv.integrationIdAndType}_${inv.entityId}`,
            }));

        onSubmit(userQuery, selectedInvoicings);
        close();
        setUserQuery('');
        setSearchFilter('');
    };

    // Filter invoicings based on search input
    const filteredInvoicings = searchFilter
        ? availableInvoicings.filter((inv) => inv.entityNumber.toLowerCase().includes(searchFilter.toLowerCase()))
        : availableInvoicings;

    if (!open) return null;

    return (
        <Dialog open={open} onClose={close} maxWidth='md' fullWidth>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <DialogTitle>{t('Need Help?')}</DialogTitle>
                <IconButton onClick={close}>
                    <Close />
                </IconButton>
            </div>
            <DialogContent>
                <Typography variant='subtitle1' gutterBottom>
                    {t('Select the invoicings you need help with')}
                </Typography>

                <TextField
                    margin='dense'
                    label={t('Search by invoice number')}
                    type='text'
                    fullWidth
                    variant='outlined'
                    value={searchFilter}
                    onChange={(e) => setSearchFilter(e.target.value)}
                    style={{ marginBottom: '1em' }}
                />

                <Paper variant='outlined' style={{ maxHeight: '200px', overflow: 'auto', marginBottom: '1em' }}>
                    <List dense>
                        {filteredInvoicings.map((invoicing) => (
                            <ListItem
                                key={invoicing.entityId}
                                secondaryAction={
                                    <Checkbox
                                        edge='end'
                                        checked={selectedInvoicingIds.includes(invoicing.entityId)}
                                        onChange={() => toggleInvoicingSelection(invoicing.entityId)}
                                    />
                                }
                            >
                                <ListItemText
                                    primary={invoicing.entityNumber}
                                    secondary={`ID: ${invoicing.entityId}`}
                                />
                            </ListItem>
                        ))}
                        {filteredInvoicings.length === 0 && (
                            <ListItem>
                                <ListItemText primary={t('No invoicings found')} />
                            </ListItem>
                        )}
                    </List>
                </Paper>

                <Typography variant='subtitle1' gutterBottom>
                    {t('Selected')} ({selectedInvoicingIds.length})
                </Typography>

                {selectedInvoicingIds.length > 0 ? (
                    <Paper
                        variant='outlined'
                        style={{
                            maxHeight: '100px',
                            overflow: 'auto',
                            marginBottom: '1em',
                            padding: '0.5em',
                        }}
                    >
                        {availableInvoicings
                            .filter((inv) => selectedInvoicingIds.includes(inv.entityId))
                            .map((invoicing) => (
                                <Typography
                                    key={invoicing.entityId}
                                    variant='body2'
                                    style={{ wordBreak: 'break-all', marginBottom: '0.25em' }}
                                >
                                    {invoicing.entityNumber}
                                </Typography>
                            ))}
                    </Paper>
                ) : (
                    <Typography variant='body2' color='text.secondary'>
                        {t('No invoicings selected')}
                    </Typography>
                )}

                <TextField
                    autoFocus
                    margin='dense'
                    id='query'
                    label={t('Describe your issue or query')}
                    type='text'
                    fullWidth
                    multiline
                    rows={4}
                    variant='outlined'
                    value={userQuery}
                    onChange={(e) => setUserQuery(e.target.value)}
                    style={{ marginTop: '1em' }}
                />
            </DialogContent>
            <DialogActions>
                <Button color='primary' onClick={close}>
                    {t('Cancel')}
                </Button>
                <Button
                    color='secondary'
                    variant='contained'
                    onClick={handleSubmit}
                    disabled={selectedInvoicingIds.length === 0 || !userQuery.trim()}
                >
                    {t('Submit')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default HelpDialog;
